/**
 * Supported UI locales for the dashboard and the helpers that resolve a raw
 * locale string to one of them. Base codes only (es, fr); regional variants
 * strip to the base. Pure so the i18n wiring and the switcher can share it and
 * it stays unit-testable without booting i18next.
 */

/** UI locales we ship. English is the source and the fallback. */
export const SUPPORTED_LOCALES = ["en", "es", "fr"] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: SupportedLocale = "en";

/** Each language named in its own language, for the picker. Endonyms are the same in every UI language, so they are not translated. */
export const LOCALE_ENDONYMS: Record<SupportedLocale, string> = {
  en: "English",
  es: "Español",
  fr: "Français",
};

const isSupported = (value: string): value is SupportedLocale =>
  (SUPPORTED_LOCALES as readonly string[]).includes(value);

/**
 * Resolves a raw locale (e.g. "es-US", "fr-CA", navigator.language) to a
 * supported base locale, falling back to English.
 *
 * @param raw - a BCP-47 tag, bare language code, or nullish.
 * @returns the supported base locale.
 */
export const resolveSupportedLocale = (raw?: string | null): SupportedLocale => {
  const primary = (raw ?? "").toLowerCase().split("-")[0];
  return isSupported(primary) ? primary : DEFAULT_LOCALE;
};

/** BCP-47 region codes the FHIR translation interceptor allowlists, keyed by base locale. */
const ACCEPT_LANGUAGE_BY_LOCALE: Partial<Record<SupportedLocale, string>> = {
  es: "es-US",
  fr: "fr-CA",
};

/**
 * Maps a raw locale to the `Accept-Language` value the FHIR translation service
 * expects, or undefined for English/unsupported. The service rewrites coded displays
 * only for the exact regional codes (es-US, fr-CA); a bare `es`/`fr` is a silent
 * passthrough, and English needs no header.
 *
 * @param raw - a BCP-47 tag, bare language code, or nullish.
 * @returns "es-US" / "fr-CA", or undefined to send no Accept-Language header.
 */
export const toAcceptLanguage = (raw?: string | null): string | undefined =>
  ACCEPT_LANGUAGE_BY_LOCALE[resolveSupportedLocale(raw)];

/**
 * Regional codes the messenger's chat-translation service tags translations with, keyed by base
 * locale. The @ethora/chat-component reader selects a message's translation by exact key
 * (`translations["fr-CA"]`), so a base code like `fr` matches nothing. Unlike
 * ACCEPT_LANGUAGE_BY_LOCALE, English maps to a real code: a reader on an English dashboard still
 * reads non-English messages translated into English.
 */
const MESSENGER_LOCALE_BY_LOCALE: Record<SupportedLocale, string> = {
  en: "en-CA",
  es: "es-US",
  fr: "fr-CA",
};

/**
 * Maps a raw locale to the regional code the messenger keys its attached chat translations by,
 * and tags outgoing messages with as their source.
 *
 * @param raw - a BCP-47 tag, bare language code, or nullish.
 * @returns "en-CA", "es-US", or "fr-CA".
 */
export const toMessengerLocale = (raw?: string | null): string =>
  MESSENGER_LOCALE_BY_LOCALE[resolveSupportedLocale(raw)];

/**
 * Sets `<html lang>` to the resolved base locale, for screen readers and
 * hyphenation. MUST be wired to i18next's `languageChanged` event.
 *
 * @param raw - the new locale, as i18next reports it.
 */
export const syncDocumentLang = (raw?: string | null): void => {
  document.documentElement.lang = resolveSupportedLocale(raw);
};
